import React from 'react';
import { Icons } from './Icon.jsx';

/**
 * Standalone component for managing applied variants on an object
 * Used in both PropertyPanel and VariantPopup for consistent variant management
 */
const VariantSelector = ({
    value = '',
    onChange,
    disabledList = [],
    onUpdateDisabled,
    onVariantEdit,
    componentType,
    label = 'Style Variants',
    hideAddButton = false
}) => {
    const variants = (value || '').split(' ').filter(Boolean);

    const handleUpdateDisabled = (newDisabled) => {
        if (onUpdateDisabled) {
            onUpdateDisabled(newDisabled);
        }
    };

    const handleRemoveVariant = (variantKey, index) => {
        // Remove from string AND disabled list
        const newVariants = variants.filter((_, idx) => idx !== index);
        const newVariantString = newVariants.length > 0 ? newVariants.join(' ') : undefined;
        onChange(newVariantString);
        if (disabledList.includes(variantKey)) {
            handleUpdateDisabled(disabledList.filter(dv => dv !== variantKey));
        }
    };

    const handleToggleDisabled = (variantKey) => {
        const isDisabled = disabledList.includes(variantKey);
        const newDisabled = isDisabled
            ? disabledList.filter(dv => dv !== variantKey)
            : [...disabledList, variantKey];
        handleUpdateDisabled(newDisabled);
    };

    return (
        <div className="mb-2">
            <label className="text-[10px] text-slate-500 block mb-1 font-semibold">{label}</label>
            <div className="space-y-1">
                {variants.map((v, i) => {
                    const isDisabled = disabledList.includes(v);
                    return (
                        <div key={i} className={`flex items-center justify-between bg-slate-100 rounded px-2 py-1 border border-slate-200 text-xs ${isDisabled ? 'opacity-50 grayscale' : ''}`}>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleToggleDisabled(v)}
                                    className={`p-1 ${isDisabled ? 'text-gray-400' : 'text-blue-500'} hover:text-blue-700`}
                                    title={isDisabled ? "Enable Variant" : "Disable Variant"}
                                >
                                    {isDisabled ? <Icons.EyeOff className="w-3 h-3"/> : <Icons.Eye className="w-3 h-3"/>}
                                </button>
                                <span
                                    className={`font-mono text-blue-700 cursor-pointer hover:underline ${isDisabled ? 'line-through' : ''}`}
                                    onClick={() => onVariantEdit && onVariantEdit({ isOpen: true, componentType: componentType, initialKey: v })}
                                    title="Edit Variant"
                                >
                                    {v}
                                </span>
                            </div>
                            <div className="flex gap-1">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveVariant(v, i);
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-500"
                                    title="Remove Variant"
                                >
                                    <Icons.Remove className="w-3 h-3"/>
                                </button>
                            </div>
                        </div>
                    );
                })}
                {!hideAddButton && (
                    <button
                        onClick={() => onVariantEdit && onVariantEdit({ isOpen: true, componentType: componentType })}
                        className="w-full text-xs py-1 border border-dashed border-slate-300 rounded text-slate-400 hover:text-blue-500 hover:border-blue-300 flex items-center justify-center gap-1"
                    >
                        <Icons.Plus className="w-3 h-3"/> Add Variant
                    </button>
                )}
            </div>
        </div>
    );
};

export default VariantSelector;
